import { Download, GripVertical, Plus, Search, Star, Trash2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { useSearch } from '@/hooks/useSearch'
import { useChartStore } from '@/stores/chartStore'
import type { Watchlist } from '@/stores/watchlistStore'
import { useWatchlistStore } from '@/stores/watchlistStore'
import { cn } from '@/utils/cn'

// ── Autocomplete search row ───────────────────────────────────────────────────

function WatchlistSearchRow({ watchlistId, onDone }: { watchlistId: string; onDone: () => void }) {
  const [query, setQuery] = useState('')
  const [focusedIdx, setFocusedIdx] = useState(-1)
  const { results, labels, isLoading } = useSearch(query)
  const { addSymbol } = useWatchlistStore()

  const allResults = Object.entries(results).flatMap(([cls, items]) =>
    items.map((item) => ({ ...item, cls }))
  )
  const open = query.length > 0

  function select(ticker: string) {
    addSymbol(watchlistId, ticker)
    setQuery('')
    setFocusedIdx(-1)
    onDone()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusedIdx((f) => Math.min(f + 1, allResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusedIdx((f) => Math.max(f - 1, -1))
    } else if (e.key === 'Enter') {
      const hit = focusedIdx >= 0 ? allResults[focusedIdx] : allResults[0]
      if (hit) {
        select(hit.ticker)
      } else {
        const manual = query.trim().toUpperCase()
        if (manual) {
          addSymbol(watchlistId, manual)
          setQuery('')
          onDone()
        }
      }
    } else if (e.key === 'Escape') {
      onDone()
    }
  }

  return (
    <div className="relative border-t border-tv-border">
      {/* Dropdown renders above the input */}
      {open && (
        <div className="absolute bottom-full left-0 right-0 z-50 max-h-72 overflow-y-auto rounded-t border border-b-0 border-tv-border bg-tv-bg-secondary shadow-lg">
          {isLoading ? (
            <div className="px-3 py-2 text-2xs text-tv-text-muted">Searching…</div>
          ) : allResults.length === 0 ? (
            <div className="px-3 py-2 text-2xs text-tv-text-muted">No results</div>
          ) : (
            Object.entries(results).map(([cls, items]) => (
              <div key={cls}>
                <div className="sticky top-0 bg-tv-bg-primary px-3 py-1 text-2xs font-semibold uppercase tracking-wide text-tv-text-muted">
                  {labels[cls] ?? cls}
                </div>
                {items.map((item) => {
                  const idx = allResults.findIndex((r) => r.ticker === item.ticker)
                  return (
                    <button
                      key={item.ticker}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        select(item.ticker)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left',
                        focusedIdx === idx ? 'bg-tv-bg-tertiary' : 'hover:bg-tv-bg-tertiary'
                      )}
                    >
                      <span className="font-mono text-xs font-semibold text-tv-text-primary">
                        {item.ticker}
                      </span>
                      <span className="truncate text-2xs text-tv-text-muted">{item.name}</span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      )}

      <div className="flex items-center gap-1 px-2 py-1.5">
        <Search className="h-3 w-3 shrink-0 text-tv-text-muted" />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setFocusedIdx(-1)
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search symbol…"
          className="flex-1 bg-transparent text-xs text-tv-text-primary placeholder-tv-text-muted outline-none"
        />
        <button
          type="button"
          onClick={onDone}
          className="rounded p-0.5 text-tv-text-muted hover:text-tv-text-primary"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

// ── Draggable symbol row ──────────────────────────────────────────────────────

function WatchlistItem({
  ticker,
  idx,
  onRemove,
  isActive,
  onClick,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
}: {
  ticker: string
  idx: number
  onRemove: () => void
  isActive: boolean
  onClick: () => void
  isDragging: boolean
  isDragOver: boolean
  onDragStart: (idx: number) => void
  onDragOver: (idx: number) => void
  onDragEnd: () => void
  onDrop: (idx: number) => void
}) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      draggable
      onDragStart={() => onDragStart(idx)}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver(idx)
      }}
      onDragEnd={onDragEnd}
      onDrop={() => onDrop(idx)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className={cn(
        'group flex cursor-pointer items-center gap-1 px-2 py-1.5 transition-colors hover:bg-tv-bg-tertiary',
        isActive && 'bg-tv-bg-tertiary',
        isDragging && 'opacity-40',
        isDragOver && 'border-t-2 border-tv-blue'
      )}
    >
      {/* Drag handle */}
      <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-tv-text-muted opacity-0 active:cursor-grabbing group-hover:opacity-60" />

      <div className="flex min-w-0 flex-1 items-center gap-1">
        <span className="truncate font-mono text-xs font-medium text-tv-text-primary">
          {ticker}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <span className="text-2xs text-tv-text-muted">—</span>
        {hovered && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="rounded p-0.5 text-tv-text-muted hover:text-tv-red"
            tabIndex={-1}
            title="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  )
}

// ── Export / Import helpers ───────────────────────────────────────────────────

function exportWatchlist(wl: Watchlist) {
  const data = JSON.stringify(
    { version: 1, watchlists: [{ name: wl.name, symbols: wl.symbols }] },
    null,
    2
  )
  const blob = new Blob([data], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${wl.name.replace(/\s+/g, '_')}_watchlist.json`
  a.click()
  URL.revokeObjectURL(url)
}

interface WatchlistImport {
  version: number
  watchlists: Array<{ name: string; symbols: string[] }>
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function WatchlistPanel() {
  const {
    watchlists,
    activeId,
    setActive,
    createWatchlist,
    deleteWatchlist,
    removeSymbol,
    reorderSymbol,
    importWatchlist,
  } = useWatchlistStore()
  const { activePanelId, setSymbol } = useChartStore()
  const [adding, setAdding] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const [confirmClear, setConfirmClear] = useState(false)

  // Drag-and-drop state
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  const importInputRef = useRef<HTMLInputElement>(null)

  const activeWatchlist = watchlists.find((w) => w.id === activeId) ?? watchlists[0]

  function handleSymbolClick(ticker: string) {
    setSymbol(activePanelId, ticker)
  }

  function startRename(id: string, currentName: string) {
    setRenamingId(id)
    setRenameValue(currentName)
  }

  function commitRename() {
    if (renamingId && renameValue.trim()) {
      useWatchlistStore.getState().renameWatchlist(renamingId, renameValue.trim())
    }
    setRenamingId(null)
  }

  function handleDragStart(idx: number) {
    setDragSrcIdx(idx)
  }

  function handleDragOver(idx: number) {
    setDragOverIdx(idx)
  }

  function handleDragEnd() {
    setDragSrcIdx(null)
    setDragOverIdx(null)
  }

  function handleDrop(toIdx: number) {
    if (dragSrcIdx !== null && activeWatchlist) {
      reorderSymbol(activeWatchlist.id, dragSrcIdx, toIdx)
    }
    setDragSrcIdx(null)
    setDragOverIdx(null)
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const raw = JSON.parse(ev.target?.result as string) as WatchlistImport
        if (raw.version === 1 && Array.isArray(raw.watchlists)) {
          for (const wl of raw.watchlists) {
            if (wl.name && Array.isArray(wl.symbols)) {
              importWatchlist(wl.name, wl.symbols)
            }
          }
        }
      } catch {
        // silently ignore malformed JSON
      }
    }
    reader.readAsText(file)
    // Reset input so the same file can be re-imported if needed
    e.target.value = ''
  }

  return (
    <aside className="flex w-56 shrink-0 flex-col border-l border-tv-border bg-tv-bg-secondary">
      {/* Panel header */}
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-tv-border px-3">
        <div className="flex items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-tv-text-secondary" />
          <span className="text-xs font-semibold text-tv-text-primary">Watchlist</span>
        </div>
        <div className="flex items-center gap-0.5">
          {/* Hidden file input for import */}
          <input
            ref={importInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImportFile}
          />
          <button
            type="button"
            title="Import watchlist (JSON)"
            onClick={() => importInputRef.current?.click()}
            className="rounded p-0.5 text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
          {activeWatchlist && (
            <button
              type="button"
              title="Export active watchlist"
              onClick={() => exportWatchlist(activeWatchlist)}
              className="rounded p-0.5 text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => createWatchlist(`List ${watchlists.length + 1}`)}
            title="New watchlist"
            className="rounded p-0.5 text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Watchlist tabs */}
      {watchlists.length > 1 && (
        <div className="scrollbar-none flex shrink-0 overflow-x-auto border-b border-tv-border">
          {watchlists.map((wl) => (
            <div key={wl.id} className="group relative flex shrink-0 items-center">
              {renamingId === wl.id ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename()
                    if (e.key === 'Escape') setRenamingId(null)
                  }}
                  className="w-20 bg-tv-bg-tertiary px-2 py-1.5 text-2xs text-tv-text-primary outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setActive(wl.id)}
                  onDoubleClick={() => startRename(wl.id, wl.name)}
                  className={cn(
                    'flex items-center gap-1 whitespace-nowrap px-2.5 py-1.5 text-2xs transition-colors',
                    wl.id === activeId
                      ? 'border-b-2 border-tv-blue text-tv-text-primary'
                      : 'text-tv-text-secondary hover:text-tv-text-primary'
                  )}
                >
                  {wl.name}
                </button>
              )}
              {watchlists.length > 1 && (
                <button
                  type="button"
                  onClick={() => deleteWatchlist(wl.id)}
                  className="hidden p-0.5 text-tv-text-muted hover:text-tv-red group-hover:flex"
                  title="Delete list"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Symbol list */}
      <div className="scrollbar-thin flex-1 overflow-y-auto">
        {activeWatchlist?.symbols.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
            <Star className="h-6 w-6 text-tv-text-muted" />
            <p className="text-xs text-tv-text-muted">No symbols yet</p>
            <p className="text-2xs text-tv-text-muted">Press + to add</p>
          </div>
        ) : (
          activeWatchlist?.symbols.map((ticker, idx) => (
            <WatchlistItem
              key={ticker}
              ticker={ticker}
              idx={idx}
              isActive={false}
              onClick={() => handleSymbolClick(ticker)}
              onRemove={() => removeSymbol(activeWatchlist.id, ticker)}
              isDragging={dragSrcIdx === idx}
              isDragOver={dragOverIdx === idx && dragSrcIdx !== idx}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
            />
          ))
        )}
      </div>

      {/* Add symbol row */}
      {adding ? (
        <WatchlistSearchRow
          watchlistId={activeWatchlist?.id ?? ''}
          onDone={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex h-8 shrink-0 items-center gap-1.5 border-t border-tv-border px-3 text-xs text-tv-text-secondary transition-colors hover:bg-tv-bg-tertiary hover:text-tv-text-primary"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add symbol</span>
        </button>
      )}

      {/* Footer */}
      <div className="flex h-6 shrink-0 items-center justify-between border-t border-tv-border px-3">
        <span className="text-2xs text-tv-text-muted">
          {activeWatchlist?.symbols.length ?? 0} symbol
          {(activeWatchlist?.symbols.length ?? 0) !== 1 ? 's' : ''}
        </span>
        {activeWatchlist &&
          activeWatchlist.symbols.length > 0 &&
          (confirmClear ? (
            <span className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  activeWatchlist.symbols.forEach((s) => removeSymbol(activeWatchlist.id, s))
                  setConfirmClear(false)
                }}
                className="text-2xs text-tv-red hover:underline"
              >
                Clear
              </button>
              <span className="text-2xs text-tv-text-muted">/</span>
              <button
                type="button"
                onClick={() => setConfirmClear(false)}
                className="text-2xs text-tv-text-muted hover:text-tv-text-primary"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              title="Clear all"
              className="text-2xs text-tv-text-muted hover:text-tv-red"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          ))}
      </div>
    </aside>
  )
}
